import { Injectable, Inject } from '@angular/core';
import { ProjectElement } from '../../resources/element/project-element.interface';
import { ProjectService } from '../../resources/project/project.service';
import { BuildingService } from '../../resources/building/building.service';
import { Area } from '../../resources/area/area.interface';
import { Resource } from '../../resources/resource.interface';

/**
 * Router service.
 *
 * This service should act as a Service for easy migration. Contains methods for
 * getting state params from AngularJS UI-Router through $transitions. After migration
 * to Angular Router, it should take the params from Router snapshots, without breaking
 * the available methods.
 */
@Injectable({
  providedIn: 'root'
})
export class RouterService {

  /** Legacy UI router $transitions hook. */
  // private $sgTransitions: any;

  /** State params. */
  private params: any;

  /** Current router state. */
  private state: string;

  /** Bind legacy $location dependency. */
  private $sgLocation: any;

  /** Bind legacy $state dependency. */
  private $sgState: any;

  constructor(
    @Inject('$sgLocation') $sgLocation: any,
    @Inject('$sgState') $sgState: any,
    @Inject('$sgTransitions') $sgTransitions: any,
    private buildingService: BuildingService,
    private projectService: ProjectService
  ) {
    this.$sgLocation = $sgLocation;
    this.$sgState = $sgState;
    $sgTransitions.onBefore({}, (transition) => {
      this.params = transition.params();
      this.state = transition.to().name;
      // console.log(transition.from());
      // console.log(transition.to());
      // console.log(transition.params());
    });
  }

  /**
   * Get state params.
   *
   * @param [key] Optional key to only get one specific state param.
   * @returns Either all state params or a specific one.
   */
  public getParams(key?: string): any {
    return !key
      ? this.params
      : this.params[key];
  }

  /**
   * Get state string.
   *
   * @returns State.
   */
  public getState(): string {
    return this.state;
  }

  /**
   * Go to area detail page.
   *
   * @todo Clean up and/or combine all the 'go to' methods.
   *
   * @param id The (construction) id of the area.
   */
  public goToArea(id: Area['_id']): void {
    this.$sgLocation.url(`/project/${ this.projectService.getId() }/building/${
      this.buildingService.getBuildingByAreaId(id)._id }/areas/${ id }`);
  }

  /**
   * Go to element page and location.
   *
   * @todo Rename 'basicArea' to 'areaTemplate'.
   *
   * @param element The element to locate.
   * @param type The element resource type.
   */
  public goToElement(element: ProjectElement, type: 'area' | 'basicArea' | 'detail' | 'pve' = 'detail'): void {
    switch (type) {
      case 'area':
        this.$sgLocation.url(`/project/${ this.projectService.getId() }/building/${
          this.buildingService.getBuildingByAreaId(element.constructionId)._id
          }/areas/${ element.constructionId }#${ element._id }`);
        break;
      case 'basicArea':
        this.$sgLocation.url(`/project/${ this.projectService.getId() }/basic-areas/${ element.constructionId }#${ element._id }`);
        break;
      case 'detail':
        this.$sgLocation.url(`/project/${ this.projectService.getId() }/elements/${ element.code }`);
        break;
      case 'pve':
        this.$sgLocation.url(`/project/${ this.projectService.getId() }/info#${ element._id }`);
        break;
    }
  }

  /**
   * Redirect to login screen.
   *
   * Makes use of the logout state, which makes sures the user is logged out front end,
   * after which the user if forwarded to the login screen.
   */
  public goToLogin(): void {
    this.$sgState.go('logout');
  }

  /**
   * Go to page.
   *
   * @todo Rename 'basicArea' to 'areaTemplate'.
   *
   * @param element The element to get the params from.
   * @param type The page type to go.
   */
  public goToPage(element: ProjectElement, type: 'area' | 'basicArea' | 'building' | 'pve'): void {
    switch (type) {
      case 'area':
        this.$sgLocation.url(`/project/${ this.projectService.getId() }/building/${
          this.buildingService.getBuildingByAreaId(element.constructionId)._id
          }/areas/${ element.constructionId }`);
        break;
      case 'basicArea':
        this.$sgLocation.url(`/project/${ this.projectService.getId() }/basic-areas/${ element.constructionId }`);
        break;
      case 'building':
        this.$sgLocation.url(`/project/${ this.projectService.getId() }/building/${ element.constructionId }/areas`);
        break;
      case 'pve':
        this.$sgLocation.url(`/project/${ this.projectService.getId() }/info`);
        break;
    }
  }

  /**
   * Check whether a certain entity is currently active.
   *
   * @param entity The entity to check.
   * @param getId When true, returns the id when entity is active (instead of boolean).
   * @returns Whether the current entity is active, either as boolean or string (entity id).
   */
  public isActive(entity: Resource['entity'], getId?: boolean): string | boolean {
    return getId
      ? Object.keys(this.getParams()).includes(entity + 'Id') && this.getParams(entity + 'Id')
      : Object.keys(this.getParams()).includes(entity + 'Id');
  }

  /**
   * Check if the current page is the element detail page.
   *
   * @returns Whether the current page is the element detail page.
   */
  public isElementDetailPage(): boolean {
    return this.getState()  === 'project/elements/element';
  }
}
